/**
 * RBAC v4 / TASK-2558 — NovoChamado MR-B
 *
 * Toggle group iconico colorido pra substituir o <Select> de tipo de chamado.
 * Cada tipo tem cor tematica, icone Tabler e tooltip de contexto.
 *
 * - Acessivel: role=radiogroup, keyboard nav (arrow keys via mantine),
 *   aria-label dinamico.
 * - Responsivo: grid 2x3 desktop, vertical em xs.
 * - Cor tematica propagada via `chamadoColor` (consumido pelo pai pra
 *   accents: borda do bloco titulo, ring do submit).
 */
import { Stack, Text, Tooltip, UnstyledButton, SimpleGrid, useMantineTheme } from '@mantine/core'
import {
  IconCircleCheck,
  IconAlertOctagon,
  IconTool,
  IconArrowsRightLeft,
  IconStackBack,
} from '@tabler/icons-react'
import type { ReactNode } from 'react'

export type ChamadoTipo =
  | 'approval'
  | 'incident'
  | 'service_request'
  | 'change_request'
  | 'gmud'

export interface TipoOption {
  value: ChamadoTipo
  label: string
  shortLabel: string
  tooltip: string
  color: string // Mantine color token (e.g. 'red', 'inovareTeal')
  icon: ReactNode
}

export const TIPO_OPTIONS: TipoOption[] = [
  {
    value: 'approval',
    label: 'Aprovação formal',
    shortLabel: 'Aprovação',
    tooltip: 'Solicitação que aguarda aprovação de gestor antes de prosseguir',
    color: 'yellow',
    icon: <IconCircleCheck size={20} />,
  },
  {
    value: 'incident',
    label: 'Incidente',
    shortLabel: 'Incidente',
    tooltip: 'Algo está quebrado e impacta operação — tratamento urgente',
    color: 'red',
    icon: <IconAlertOctagon size={20} />,
  },
  {
    value: 'service_request',
    label: 'Solicitação',
    shortLabel: 'Solicitação',
    tooltip: 'Pedido de serviço sem caráter emergencial',
    color: 'inovareTeal',
    icon: <IconTool size={20} />,
  },
  {
    value: 'change_request',
    label: 'Mudança',
    shortLabel: 'Mudança',
    tooltip: 'Alteração planejada em configuração ou recurso',
    color: 'orange',
    icon: <IconArrowsRightLeft size={20} />,
  },
  {
    value: 'gmud',
    label: 'GMUD',
    shortLabel: 'GMUD',
    tooltip: 'Gestão de Mudança — processo formal com janela e aprovação',
    color: 'indigo',
    icon: <IconStackBack size={20} />,
  },
]

export function getTipoOption(value: string): TipoOption | undefined {
  return TIPO_OPTIONS.find((t) => t.value === value)
}

interface Props {
  value: ChamadoTipo
  onChange: (v: ChamadoTipo) => void
  label?: string
}

export function TipoChamadoToggleGroup({ value, onChange, label = 'Tipo de chamado' }: Props) {
  const theme = useMantineTheme()
  return (
    <Stack gap={6}>
      <Text size="xs" fw={500} c="dimmed">{label}</Text>
      <SimpleGrid
        cols={{ base: 1, xs: 2, md: 3 }}
        spacing={6}
        role="radiogroup"
        aria-label={label}
      >
        {TIPO_OPTIONS.map((opt) => {
          const selected = opt.value === value
          const colorShade = theme.colors[opt.color]?.[6] ?? theme.colors.gray[6]
          const colorBgShade = theme.colors[opt.color]?.[0] ?? theme.colors.gray[0]
          return (
            <Tooltip
              key={opt.value}
              label={opt.tooltip}
              position="top"
              withArrow
              openDelay={500}
            >
              <UnstyledButton
                role="radio"
                aria-checked={selected}
                aria-label={opt.label}
                onClick={() => onChange(opt.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault()
                    const idx = TIPO_OPTIONS.findIndex((o) => o.value === value)
                    onChange(TIPO_OPTIONS[(idx + 1) % TIPO_OPTIONS.length].value)
                  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault()
                    const idx = TIPO_OPTIONS.findIndex((o) => o.value === value)
                    onChange(TIPO_OPTIONS[(idx - 1 + TIPO_OPTIONS.length) % TIPO_OPTIONS.length].value)
                  }
                }}
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: `1.5px solid ${selected ? colorShade : 'var(--mantine-color-gray-3)'}`,
                  background: selected ? colorBgShade : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  transition: 'all 120ms ease',
                }}
              >
                <span style={{ color: colorShade, display: 'flex' }}>{opt.icon}</span>
                <Text
                  size="xs"
                  fw={selected ? 600 : 500}
                  c={selected ? colorShade : 'gray.7'}
                  style={{ lineHeight: 1.2 }}
                >
                  {opt.shortLabel}
                </Text>
              </UnstyledButton>
            </Tooltip>
          )
        })}
      </SimpleGrid>
    </Stack>
  )
}
